/**
 * URL-driven navigation, so every view is a shareable link.
 *
 * The panel used to hold its whole location in React state — a `tab` variable
 * and a `selected family` variable — which meant the address bar always read
 * `/` no matter what you were looking at. An admin who found something worth a
 * second opinion had no way to send a colleague to it, and a refresh threw the
 * view away.
 *
 * Paths, not a hash. This makes ONE DEMAND OF WHOEVER PUBLISHES THIS BUNDLE:
 * the host must serve `/index.html` for any path that is not a real file, or
 * `/families/fam-123` 404s instead of opening the app. CloudFront did it for
 * the AWS build; whatever replaces it has to do the same, and this comment is
 * where that requirement is written down. A hash would need no such fallback,
 * but hash URLs are second-class — they do not survive being pasted into tools
 * that strip fragments, and they read as a workaround.
 *
 * Deliberately hand-rolled rather than a router dependency: this is one regex
 * and a popstate listener against five routes. A router earns its place when
 * there are nested layouts and loaders to coordinate, and there are not.
 */
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'dashboard' }
  | { name: 'pipeline'; status?: string; familyId?: string; kind?: string }
  | { name: 'commission'; commissionId: string }
  | { name: 'executions'; status?: string }
  | { name: 'families'; familyId?: string }
  | { name: 'users'; filter?: string }
  | { name: 'videos'; videoId?: string; familyId?: string }
  | { name: 'clips'; kind?: string; assetId?: string; familyId?: string }
  | { name: 'characters'; familyId?: string }
  | { name: 'verdicts'; allowed?: string }
  | { name: 'devices'; familyId?: string }
  | { name: 'ledger'; familyId?: string }
  | { name: 'config' }
  | { name: 'health' };

/** Every tab in the header. `commission` is excluded because it is a detail
 *  route with a required id — there is no bare /commissions to link to. */
export type TabName = Exclude<Route['name'], 'commission'>;

export function parse(path: string, search = ''): Route {
  const params = new URLSearchParams(search);
  const parts = path.replace(/^\/+|\/+$/g, '').split('/');
  const family = params.get('family') || undefined;

  switch (parts[0]) {
    case 'pipeline':
      return {
        name: 'pipeline',
        status: params.get('status') || undefined,
        familyId: family,
        kind: params.get('kind') || undefined,
      };
    case 'commissions':
      // A commission id is the whole point of this route, so a bare
      // /commissions is the pipeline list rather than a broken detail page.
      return parts[1]
        ? { name: 'commission', commissionId: parts[1] }
        : { name: 'pipeline' };
    case 'executions':
      return { name: 'executions', status: params.get('status') || undefined };
    case 'families':
      // /families/<id> deep-links straight to one family's detail.
      return { name: 'families', familyId: parts[1] || undefined };
    case 'users':
      return { name: 'users', filter: params.get('q') || undefined };
    case 'videos':
      return { name: 'videos', videoId: parts[1] || undefined, familyId: family };
    case 'ledger':
      return { name: 'ledger', familyId: family };
    case 'clips':
      return {
        name: 'clips',
        assetId: parts[1] || undefined,
        kind: params.get('kind') || undefined,
        familyId: family,
      };
    case 'characters':
      return { name: 'characters', familyId: family };
    case 'verdicts':
      return { name: 'verdicts', allowed: params.get('allowed') || undefined };
    case 'devices':
      return { name: 'devices', familyId: family };
    case 'config':
      return { name: 'config' };
    case 'health':
      return { name: 'health' };
    default:
      return { name: 'dashboard' };
  }
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const out = search.toString();
  return out ? `?${out}` : '';
}

export function href(route: Route): string {
  switch (route.name) {
    case 'pipeline':
      return (
        '/pipeline' +
        query({ status: route.status, family: route.familyId, kind: route.kind })
      );
    case 'commission':
      return `/commissions/${encodeURIComponent(route.commissionId)}`;
    case 'executions':
      return '/executions' + query({ status: route.status });
    case 'families':
      return route.familyId ? `/families/${encodeURIComponent(route.familyId)}` : '/families';
    case 'users':
      return '/users' + query({ q: route.filter });
    case 'videos':
      return route.videoId
        ? `/videos/${encodeURIComponent(route.videoId)}`
        : '/videos' + query({ family: route.familyId });
    case 'ledger':
      return '/ledger' + query({ family: route.familyId });
    case 'clips':
      return route.assetId
        ? `/clips/${encodeURIComponent(route.assetId)}`
        : '/clips' + query({ kind: route.kind, family: route.familyId });
    case 'characters':
      return '/characters' + query({ family: route.familyId });
    case 'verdicts':
      return '/verdicts' + query({ allowed: route.allowed });
    case 'devices':
      return '/devices' + query({ family: route.familyId });
    case 'config':
      return '/config';
    case 'health':
      return '/health';
    default:
      return '/';
  }
}

/** Navigate without a reload, and leave a real entry in the back stack. */
export function go(route: Route): void {
  const next = href(route);
  if (next !== window.location.pathname + window.location.search) {
    window.history.pushState({}, '', next);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Replace rather than push — for a filter that changes as someone types, so
 * the back button returns to the previous SCREEN rather than walking back
 * through every keystroke.
 */
export function replace(route: Route): void {
  window.history.replaceState({}, '', href(route));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parse(window.location.pathname, window.location.search),
  );
  useEffect(() => {
    const onPop = () =>
      setRoute(parse(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return route;
}
